import React from 'react';

/**
 * What a resumed build says about the keys before it carries on.
 *
 * <h3>Why this warns rather than blocks</h3>
 *
 * The gateway and SDK plaintext appear on the wire exactly once, at the mint.
 * Only bcrypt hashes are kept, and nothing — not this surface, not the
 * platform — can return them. A build resumed past that point therefore cannot
 * show them again.
 *
 * Whether they were saved is something only the person knows. A checkbox
 * asserting it would collect a click, not a fact, and would stop somebody who
 * did save them from getting on with it. So the warning is plain, carrying on
 * is not obstructed, and the way out sits beside it for whoever needs it.
 *
 * <h3>Why starting over says what it costs</h3>
 *
 * It registers a second agent with its own keys; the first stays on the
 * record. Somebody choosing it should know that before they click, not after.
 */
export default function ResumeKeysWarning({
  agentName, onContinue, onStartOver, busy = false,
}) {
  return (
    <div className="p7-note p7-note--hold"
         style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
      <p style={NOTE}>
        {agentName ? <strong>{agentName}</strong> : 'This agent'} was registered earlier, and its
        keys were <strong>shown once</strong> at that point. They cannot be shown again — only
        hashes are kept.
      </p>
      <p style={NOTE}>
        <strong>Do not carry on if you did not save them.</strong> The rest of this leaves you
        holding an agent you cannot wire up. A fresh key can be reissued from the agent&rsquo;s own
        page in the full platform, or you can start over here — which registers a new agent with
        new keys and leaves this one on the record.
      </p>

      <div style={{ display: 'flex', gap: 'var(--p7-space-2)', flexWrap: 'wrap' }}>
        <button type="button" className="p7-btn p7-btn--primary" disabled={busy}
                onClick={() => onContinue && onContinue()}>
          Continue the build
        </button>
        <button type="button" className="p7-btn" disabled={busy}
                onClick={() => onStartOver && onStartOver()}>
          Start over
        </button>
      </div>
    </div>
  );
}

const NOTE = {
  margin: 0, font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
  color: 'var(--p7-ink-subtle)', lineHeight: 1.55,
};
